import { defineStore } from 'pinia';
import { getTimeRanges } from 'ecommerce/services/boxCatalogView';
import { getPaginatedBoxActivities } from 'ecommerce/services/activities.service';
import dayjs from 'dayjs';
import apolloClient from 'ecommerce/utils/apolloClient';

import ActivityBySlug from 'ecommerce/assets/graphql/queries/Activity.graphql';

const INITIAL_PAGE_CONFIG = {
  INITIAL_PAGE: 1,
  DEFAULT_PER_PAGE: 12,
};

const DEFAULT_FILTERS = {
  order_by: '',
  province_id: null,
  search_term: '',
  at_home: false,
  category_id:''
};

export const useBoxCatalogViewStore = defineStore('boxCatalogContext', {
  state: () => ({
    flags: {
      initialDataLoading: true,
      loading: false,
      alreadySearchedByDate: false,
      isModalReservationOpen: false,
    },
    filters: JSON.parse(JSON.stringify(DEFAULT_FILTERS)),
    time_range: {
      date: null,
      time_range_slug: null,
    },
    page_config: {
      page_number: INITIAL_PAGE_CONFIG.INITIAL_PAGE,
      per_page: INITIAL_PAGE_CONFIG.DEFAULT_PER_PAGE,
    },
    selectedSchedule: null,
    paginatedActivities: {
      allActivitiesResults: [],
      currentPageInfo: null,
    },
    otherActivities: [],
    allTimeRanges: [],
    alreadySearchedByDate: false,
    titleDate: null,
    paginated_activities: [],
    isModalReservationOpen: false,
    catalogCodeComponent: null,
    catalogModalComponent: null,
    expirationDate: null,
    isATT: false,
  }),
  getters: {
    getLoading: state => state.flags.loading,
    getInitialDataLoading: state => state.flags.initialDataLoading,
    getFilters: state => state.filters,
    getTimeRange: state => state.time_range,
    getSelectedSchedule: state => state.selectedSchedule,
    getPageConfig: state => state.page_config,
    getPageInfo: state => state.paginatedActivities.currentPageInfo,
    getHasNextPage: state =>
      state.paginatedActivities.currentPageInfo?.has_next || false,
    getTotalActivities: state =>
      state.paginatedActivities.currentPageInfo?.count,
    paginatedBoxActivitiesResults: state =>
      state.paginatedActivities.allActivitiesResults || [],
    getOtherActivities: state => state.otherActivities,
    getTitleDate: state => state.titleDate,
    getIsModalReservationOpen: state => state.flags.isModalReservationOpen,
    getCatalogCodeComponent: state => state.catalogCodeComponent,
    getCatalogModalComponent: state => state.catalogModalComponent,
    getExpirationDate: state => state.expirationDate || false,
  },
  actions: {
    async init(box_slug) {
      try {
        this.resetFilters();
        await Promise.all([
          this.getPaginatedBoxActivities(box_slug),
          this.getTimeRanges(),
        ]);
        this.flags.initialDataLoading = false;
      } catch (err) {
        /* eslint-disable-next-line */
        console.error('Error on boxCatalogView store init', err);
      }
    },

    async getPaginatedBoxActivities(box_slug) {
      this.flags.loading = true;

      const { response, others } = await getPaginatedBoxActivities(
        box_slug,
        this.getPageConfig,
        this.getFilters,
        this.getTimeRange,
      );

      this.otherActivities = others;
      this.paginatedActivities.currentPageInfo = response.page_info;
      this.paginatedActivities.allActivitiesResults = [
        ...this.paginatedActivities.allActivitiesResults,
        ...response.results,
      ];
      this.flags.loading = false;
    },

    async getTimeRanges() {
      this.flags.loading = true;
      this.allTimeRanges = await getTimeRanges();
      this.allTimeRanges.sort((a, b) => {
        const startTimeA = parseInt(a.start.split(':')[0]);
        const startTimeB = parseInt(b.start.split(':')[0]);
        return startTimeA - startTimeB;
      });
      this.flags.loading = false;
    },

    /* Date fields */
    onSetTimeSlot(timeSlotSlug) {
      this.time_range.time_range_slug = timeSlotSlug;
    },

    onSetDate(selectedDate) {
      this.time_range.date = selectedDate;
    },

    onSetSchedule(shedule) {
      this.selectedSchedule = shedule;
    },

    /* Filters fields */
    onSetProvince(provinceId) {
      this.resetPageConfig();
      this.filters.province_id = provinceId == 0 ? undefined : provinceId;
    },

    onSetCategory(categoryId) {
      this.resetPageConfig();
      this.filters.category_id = categoryId == 0 ? undefined : categoryId;
    },

    onSetOrderBy(orderBy) {
      this.filters.order_by = orderBy;
    },

    onSetSearchTerm(searchTerm) {
      this.filters.search_term = searchTerm;
    },

    onSetAtHome(value) {
      this.filters.at_home = value;
    },

    /* Page config */
    onSetPage(pageNumber) {
      this.page_config.page_number = pageNumber;
    },

    onSetPerPage(productsQuantity) {
      this.page_config.per_page = productsQuantity;
    },

    resetPageConfig() {
      this.page_config.page_number = INITIAL_PAGE_CONFIG.INITIAL_PAGE;
      this.page_config.per_page = INITIAL_PAGE_CONFIG.DEFAULT_PER_PAGE;
      this.paginatedActivities.allActivitiesResults = [];
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
    },

    generateTitleDate() {
      if (!this.time_range.date && !this.time_range.time_range_slug)
        this.titleDate = null;
      const date = dayjs(this.time_range.date);
      const formattedDateName = date.format('dddd');
      const capitalizedDateName =
        formattedDateName.charAt(0).toUpperCase() + formattedDateName.slice(1);
      const formattedDateNumber = date.format('DD/MM');
      this.titleDate = { capitalizedDateName, formattedDateNumber };
    },

    /* Modal reservation */
    onToggleModalReservation() {
      this.flags.isModalReservationOpen = !this.flags.isModalReservationOpen;
    },

    async onActivityBySlug(slug) {
      try {
        const { data } = await apolloClient.query({
          query: ActivityBySlug,
          variables: { slug },
        });
        const { activity } = data;
        const { breadcrumb, catalog_case, is_catalog, gift_information } =
          activity.activity_view_config;
        const breadCrumb = JSON.parse(breadcrumb);
        const giftInformation = JSON.parse(gift_information);
        const catalogCase = JSON.parse(catalog_case);
        const isCatalog = JSON.parse(is_catalog);
        const isReserved = ['reserved', 'validated_and_not_reserved'].includes(
          giftInformation.status,
        );
        activity.locations =
          activity.partneractivity?.locations ||
          activity.comboactivity.locations;

        if (isCatalog) return;
        if (!catalogCase) return;

        this.catalogCodeComponent = {
          name: catalogCase.code_component,
          props: {
            code: catalogCase.code,
            activity,
            catalogColor: breadCrumb.color,
            reservedGift: giftInformation,
            isReserved,
          },
        };

        this.catalogModalComponent = {
          name: catalogCase.modal_component,
          props: {
            title: 'Reservá esta experiencia',
            code: catalogCase.code,
            codeId: catalogCase.code_id,
            categoryColor: breadCrumb.color,
            locations: activity.locations,
            activity,
            isReserved,
            reserverdGift: giftInformation,
            codeExpirationDate: giftInformation.expiration_date,
          },
        };

        return activity;
      } catch (error) {
        return error;
      }
    },

    onCreateReservation(status) {
      if (status.success) {
        this.flags.isModalReservationOpen = false;
      }
    },

    scrollToResultsStart() {
      const activitiesResultElement = document.getElementById(
        'catalogviewResultsContainer',
      );
      activitiesResultElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },

    cleanAvailability() {
      this.time_range.date = null;
      this.time_range.time_range_slug = null;
      this.resetPageConfig();
      this.flags.alreadySearchedByDate = false;
    },

    setATT (isATT = false) {
      this.isATT = isATT;
    }
  },
});
